import { getLocalTimeZone, today } from "@internationalized/date";
import { Plus, SearchLg } from "@untitledui/icons";
import { type ReactNode, useEffect, useId, useState } from "react";
import type { DateValue } from "react-aria-components";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Button as PageButton } from "@/shared/components/button/button-untitledui";
import {
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterField,
} from "@/shared/components/filter-bar";
import { Input as SharedInput } from "@/shared/components/input";
import {
	PageHeader,
	type PageHeaderTitleLevel,
} from "@/shared/components/page-header";
import { PaginationPageDefault } from "@/shared/components/pagination";
import {
	type SelectItemType,
	Select as SharedSelect,
} from "@/shared/components/select";
import {
	resolveTableViewState,
	TABLE_STATE_DEFAULT_MESSAGES,
	Table,
	TableCard,
	type TableRowAction,
	TableRowActions,
	TableStateRow,
} from "@/shared/components/table";
import { cx } from "@/utils/cx";

export type Column<T> = {
	id: string;
	label: string;
	minWidth?: number;
	render?: (item: T) => React.ReactNode;
};

export type Action<T> = {
	onClick: (item: T) => void;
	label: string | ((item: T) => string);
	icon?: React.ComponentType<{ className?: string }>;
	disabled?: boolean | ((item: T) => boolean);
};

function resolveActionLabel<T>(action: Action<T>, item: T): string {
	return typeof action.label === "function" ? action.label(item) : action.label;
}

function resolveActionDisabled<T>(action: Action<T>, item: T): boolean {
	return typeof action.disabled === "function"
		? action.disabled(item)
		: Boolean(action.disabled);
}

export type TableCustomProps<T extends { id: string | number }> = {
	title: string | ReactNode;
	badge?: React.ReactNode;
	data: T[];
	columns: Column<T>[];
	actions?: Action<T>[];
	onAdd?: () => void;
	addButtonLabel?: string;

	searchPlaceholder?: string;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	disableSearch?: boolean;

	enableSelection?: boolean;
	currentPage?: number;
	totalPages?: number | null;
	onPageChange?: (page: number) => void;

	filterPlaceholder?: string;
	filterValue?: string;
	filterOptions?: Array<{
		id: string;
		label: string;
		icon?: React.ComponentType<{ className?: string }>;
	}>;
	onFilterChange?: (value: string) => void;

	selectedDate?: DateValue | null;
	onDateChange?: (date: DateValue | null) => void;

	noDataMessage?: string | ReactNode;

	extraActions?: ReactNode;
	beforeTable?: ReactNode;
	actionsLabel?: string;
	onRowClick?: (item: T) => void;
	isLoading?: boolean;
	isError?: boolean;
	errorMessage?: ReactNode;
	onRetry?: () => void;

	/**
	 * Composição do topo da tela.
	 *
	 * - `legacy` (padrão): título, busca e botões anteriores. Mantido para os
	 *   consumidores que ainda não migraram.
	 * - `page`: `PageHeader` com a tipografia de `/infractions` e `FilterBar`
	 *   com busca, filtros e ações. `extraActions` vai para as ações da barra,
	 *   antes do botão de criação, que passa a ser primário `sm`.
	 */
	layout?: "legacy" | "page";
	/** Subtítulo do cabeçalho. Só no layout `page`. */
	subtitle?: ReactNode;
	/** Nível do título no layout `page`. Use `h2` quando a página já tiver um `h1`. */
	titleAs?: PageHeaderTitleLevel;
	/** Classes extras do título no layout `page`. */
	titleClassName?: string;
	/** Nome acessível da barra de filtros no layout `page`. */
	filterBarLabel?: string;
	/** Nome acessível da busca no layout `page`. Padrão: o placeholder. */
	searchLabel?: string;
	/** Campos de filtro extras, exibidos depois da busca e dos filtros padrão. Só no layout `page`. */
	filters?: ReactNode;
	/** Classes extras do contêiner externo, por exemplo para remover o padding quando a tabela fica dentro de outra página. */
	className?: string;
};

type TableCustomPageToolbarProps = Pick<
	TableCustomProps<{ id: string }>,
	| "addButtonLabel"
	| "badge"
	| "disableSearch"
	| "extraActions"
	| "filterBarLabel"
	| "filterOptions"
	| "filterPlaceholder"
	| "filters"
	| "filterValue"
	| "onAdd"
	| "onDateChange"
	| "onFilterChange"
	| "searchLabel"
	| "searchPlaceholder"
	| "searchValue"
	| "selectedDate"
	| "subtitle"
	| "title"
	| "titleAs"
	| "titleClassName"
> & {
	onSearchChange: (value: string) => void;
	filterOptions: NonNullable<TableCustomProps<{ id: string }>["filterOptions"]>;
	searchPlaceholder: string;
	filterPlaceholder: string;
	filterBarLabel: string;
};

/** Topo do layout `page`: `PageHeader` e `FilterBar` com busca, filtros e ações. */
function TableCustomPageToolbar({
	addButtonLabel,
	badge,
	disableSearch,
	extraActions,
	filterBarLabel,
	filterOptions,
	filterPlaceholder,
	filters,
	filterValue,
	onAdd,
	onDateChange,
	onFilterChange,
	onSearchChange,
	searchLabel,
	searchPlaceholder,
	searchValue,
	selectedDate,
	subtitle,
	title,
	titleAs,
	titleClassName,
}: TableCustomPageToolbarProps) {
	const searchInputId = useId();
	const hasActions = Boolean(extraActions) || Boolean(onAdd);

	return (
		<>
			<PageHeader
				subtitle={subtitle}
				title={
					badge ? (
						<span className="inline-flex flex-wrap items-center gap-2">
							{title}
							{badge}
						</span>
					) : (
						title
					)
				}
				titleAs={titleAs}
				titleClassName={titleClassName}
			/>

			<FilterBar
				actions={
					hasActions ? (
						<>
							{extraActions}
							{onAdd ? (
								<PageButton
									color="primary"
									iconLeading={Plus}
									onClick={onAdd}
									size="sm"
								>
									{addButtonLabel}
								</PageButton>
							) : null}
						</>
					) : undefined
				}
				label={filterBarLabel}
			>
				<FilterField
					className="sm:max-w-[600px]"
					grow
					htmlFor={searchInputId}
					label={searchLabel ?? searchPlaceholder}
					labelVisibility="hidden"
				>
					<div className="relative w-full sm:max-w-[600px]">
						<SearchLg
							aria-hidden
							className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-fg-quaternary"
						/>
						<SharedInput
							className={cx(FILTER_CONTROL_HEIGHT_CLASS, "pl-9")}
							disabled={disableSearch}
							id={searchInputId}
							maxLength={80}
							onChange={(event) => onSearchChange(event.target.value)}
							placeholder={searchPlaceholder}
							type="text"
							value={searchValue ?? ""}
						/>
					</div>
				</FilterField>

				{filterOptions.length > 0 ? (
					<FilterField label={filterPlaceholder} labelVisibility="hidden">
						<SharedSelect
							aria-label={filterPlaceholder}
							className="w-full"
							isClearable={false}
							items={filterOptions.map((option) => ({
								id: option.id,
								label: option.label,
								// Os ícones de filtro são componentes de função (Untitled UI).
								icon: option.icon as SelectItemType["icon"],
							}))}
							onChange={(key) => {
								if (key !== null && key !== undefined) {
									onFilterChange?.(String(key));
								}
							}}
							placeholder={filterPlaceholder}
							size="sm"
							value={filterValue ?? null}
						>
							{(item) => (
								<SharedSelect.Item icon={item.icon} id={item.id}>
									{item.label}
								</SharedSelect.Item>
							)}
						</SharedSelect>
					</FilterField>
				) : null}

				{onDateChange ? (
					<FilterField label="Data" labelVisibility="hidden">
						<DatePicker
							aria-label="Filtrar por data"
							maxValue={today(getLocalTimeZone())}
							onApply={(date) => {
								onDateChange(date);
							}}
							onChange={(date) => {
								onDateChange(date);
							}}
							triggerSize="sm"
							value={selectedDate}
						/>
					</FilterField>
				) : null}

				{filters}
			</FilterBar>
		</>
	);
}

export function TableCustom<T extends { id: string | number }>({
	title,
	badge,
	data,
	columns,
	actions = [],
	onAdd,
	addButtonLabel = "Adicionar novo",
	searchPlaceholder = "Pesquisar...",
	onSearchChange,
	searchValue,
	disableSearch = false,
	enableSelection = false,

	currentPage = 1,
	totalPages = null,
	onPageChange,

	filterPlaceholder = "Filtrar por...",
	filterValue,
	filterOptions = [],
	onFilterChange,
	noDataMessage,

	onDateChange,
	selectedDate,
	extraActions,
	beforeTable,
	actionsLabel = "Ações",
	onRowClick,
	isLoading = false,
	isError = false,
	errorMessage,
	onRetry,
	layout = "legacy",
	subtitle,
	titleAs,
	titleClassName,
	filterBarLabel = "Filtros",
	searchLabel,
	filters,
	className,
}: TableCustomProps<T>) {
	const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(
		new Set()
	);

	useEffect(() => {
		setSelectedKeys(new Set());
	}, [data]);

	const ariaLabel =
		typeof title === "string" && title.trim().length > 0 ? title : "";

	const getItemValue = (item: T, columnId: keyof T) =>
		String(item[columnId] ?? "");

	const handleSelectionChange = (
		keys: string | number | Set<string | number>
	) => {
		if (keys instanceof Set) {
			setSelectedKeys(keys);
		} else {
			setSelectedKeys(new Set([keys]));
		}
	};

	const columnCount = columns.length + (actions.length > 0 ? 1 : 0);
	const { view, isRefreshing, hasRefreshError } = resolveTableViewState({
		itemCount: data.length,
		isLoading,
		isError,
	});
	const showPagination = typeof totalPages === "number" && totalPages > 1;

	const handleSearchChange = (value: string) => {
		if (value.length > 80) {
			return;
		}
		onSearchChange?.(value);
	};

	const pageToolbar = (
		<TableCustomPageToolbar
			addButtonLabel={addButtonLabel}
			badge={badge}
			disableSearch={disableSearch}
			extraActions={extraActions}
			filterBarLabel={filterBarLabel}
			filterOptions={filterOptions}
			filterPlaceholder={filterPlaceholder}
			filters={filters}
			filterValue={filterValue}
			onAdd={onAdd}
			onDateChange={onDateChange}
			onFilterChange={onFilterChange}
			onSearchChange={handleSearchChange}
			searchLabel={searchLabel}
			searchPlaceholder={searchPlaceholder}
			searchValue={searchValue}
			selectedDate={selectedDate}
			subtitle={subtitle}
			title={title}
			titleAs={titleAs}
			titleClassName={titleClassName}
		/>
	);
	const legacyToolbar = (
		<>
			<header className="space-y-2">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<h1 className="font-bold text-2xl text-secondary leading-8">
						{title}
					</h1>
					{badge}
				</div>
			</header>

			<div className="flex flex-col gap-3 sm:flex-row">
				<div className="flex min-w-0 gap-3">
					<Input
						className="w-full max-w-[323px]"
						icon={SearchLg}
						isDisabled={disableSearch}
						maxLength={80}
						onChange={handleSearchChange}
						placeholder={searchPlaceholder}
						type="text"
						value={searchValue}
					/>

					{filterOptions.length > 0 && (
						<Select
							aria-label={filterPlaceholder}
							className="w-full max-w-[200px]"
							items={filterOptions.map((option) => ({
								id: option.id,
								label: option.label,
								icon: option.icon
									? (props) => {
											const Icon = option.icon!;
											return <Icon {...props} />;
										}
									: undefined,
							}))}
							onSelectionChange={(key) => onFilterChange?.(String(key))}
							placeholder={filterPlaceholder}
							selectedKey={filterValue}
						>
							{(item) => (
								<Select.Item icon={item.icon} id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}

					{onDateChange && (
						<DatePicker
							aria-label="Filtrar por data"
							maxValue={today(getLocalTimeZone())}
							onApply={(date) => {
								onDateChange(date);
							}}
							onChange={(date) => {
								onDateChange(date);
							}}
							value={selectedDate}
						/>
					)}
				</div>
				{!!extraActions && (
					<div className="flex flex-1 gap-3">{extraActions}</div>
				)}

				{onAdd && (
					<Button onClick={onAdd} size="md">
						<span className="flex items-center gap-2">
							<Plus className="size-5" color="#84CAFF" />
							{addButtonLabel}
						</span>
					</Button>
				)}
			</div>
		</>
	);

	return (
		<div className={cx("w-full space-y-6 px-6 py-8", className)}>
			{layout === "page" ? pageToolbar : legacyToolbar}

			{beforeTable}

			<TableCard.Root variant="listing">
				<Table
					aria-label={ariaLabel}
					isRefreshing={isRefreshing}
					onSelectionChange={handleSelectionChange}
					selectedKeys={selectedKeys}
					selectionMode={enableSelection ? "multiple" : "none"}
				>
					<Table.Header>
						{columns.map((column, index) => (
							<Table.Head
								id={column.id}
								isRowHeader={index === 0}
								key={column.id}
								label={column.label}
								style={{
									...(!!column?.minWidth && {
										minWidth: `${column.minWidth}px`,
									}),
								}}
							/>
						))}
						{actions.length > 0 && (
							<Table.Head className="w-12" id="actions" label={actionsLabel} />
						)}
					</Table.Header>

					<Table.Body>
						{hasRefreshError ? (
							<TableStateRow
								colSpan={columnCount}
								onRetry={onRetry}
								state="refresh-error"
							/>
						) : null}
						{view === "data" &&
							data.map((item) => (
								<Table.Row
									className={
										onRowClick
											? "cursor-pointer hover:bg-secondary_subtle"
											: undefined
									}
									id={item.id}
									key={item.id}
									onAction={
										onRowClick
											? () => {
													onRowClick(item);
												}
											: undefined
									}
								>
									{columns.map((column) => (
										<Table.Cell key={column.id}>
											{column.render
												? column.render(item)
												: getItemValue(item, column.id as keyof T)}
										</Table.Cell>
									))}

									{actions.length > 0 && (
										<Table.Cell>
											<TableRowActions
												actions={actions.map((action, idx) => ({
													id: `${item.id}-${idx}`,
													label: resolveActionLabel(action, item),
													// Os ícones de ação são componentes de função (Untitled UI).
													icon: action.icon as TableRowAction["icon"],
													isDisabled: resolveActionDisabled(action, item),
													onClick: () => action.onClick(item),
												}))}
											/>
										</Table.Cell>
									)}
								</Table.Row>
							))}
						{view !== "data" && (
							<TableStateRow
								colSpan={columnCount}
								emptyContent={
									noDataMessage || TABLE_STATE_DEFAULT_MESSAGES.empty
								}
								errorMessage={errorMessage}
								onRetry={onRetry}
								state={view}
							/>
						)}
					</Table.Body>
				</Table>

				{showPagination ? (
					<PaginationPageDefault
						className="p-5"
						onPageChange={onPageChange}
						page={currentPage}
						total={totalPages}
					/>
				) : null}
			</TableCard.Root>
		</div>
	);
}
